/** ESC/POS control bytes (subset for thermal 58/80mm). */

export const ESC = 0x1b;
export const GS = 0x1d;
export const LF = 0x0a;

/** Initialize printer */
export function cmdInit(): Uint8Array {
  return new Uint8Array([ESC, 0x40]);
}

/** Align: 0 left, 1 center, 2 right */
export function cmdAlign(align: 0 | 1 | 2): Uint8Array {
  return new Uint8Array([ESC, 0x61, align]);
}

/** Bold on/off */
export function cmdBold(on: boolean): Uint8Array {
  return new Uint8Array([ESC, 0x45, on ? 1 : 0]);
}

/** Text size (simple double height/width bits — ESC ! n) */
export function cmdTextSize(normal: boolean): Uint8Array {
  return new Uint8Array([ESC, 0x21, normal ? 0 : 0x30]);
}

/** Feed and partial cut */
export function cmdCut(): Uint8Array {
  return new Uint8Array([GS, 0x56, 0x41, 0x03]);
}

/** Full cut (GS V 0) — some 58 mm portables ignore partial cut (GS V A …) over BLE. */
export function cmdCutFull(): Uint8Array {
  return new Uint8Array([GS, 0x56, 0x00]);
}

/** Feed lines */
export function cmdFeedLines(n: number): Uint8Array {
  const lines = Math.min(255, Math.max(0, Math.floor(n)));
  return new Uint8Array([ESC, 0x64, lines]);
}

/**
 * Trailing flush so the print engine commits buffered text before cut (XP-P203A / BLE bridges).
 * LF + ESC d 1 + extra feed lines.
 */
export function appendEscPosJobFlush(parts: Uint8Array[], feedBeforeCut = 6): void {
  parts.push(new Uint8Array([LF]));
  parts.push(cmdFeedLines(1));
  parts.push(cmdFeedLines(feedBeforeCut));
  parts.push(cmdCutFull());
}

/**
 * ISSC UART bridges: feed paper only (no GS V cut — many ISSC modules ignore or stall on cut).
 */
export function appendEscPosJobFlushIssc(parts: Uint8Array[], feedLines = 8): void {
  parts.push(new Uint8Array([LF]));
  parts.push(cmdFeedLines(1));
  parts.push(cmdFeedLines(feedLines));
  parts.push(new Uint8Array([ESC, 0x4a, 0x48]));
}

/** Encode UTF-8 string to bytes */
export function encodeUtf8(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

/** Concatenate Uint8Array chunks */
export function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const len = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(len);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

/**
 * ESC/POS QR Code (Model 2) — GS ( k ... fn
 * Simplified sequence: select model, set size, store data, print.
 */
export function cmdQrCode(data: string, sizeModule = 6): Uint8Array {
  const bytes = encodeUtf8(data);
  if (bytes.length === 0) return new Uint8Array(0);

  const chunks: Uint8Array[] = [];

  // fn=65: QR Code: Select the model (Model 2)
  chunks.push(new Uint8Array([GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00]));

  // fn=67: set module size (1–16 typical)
  const mod = Math.min(16, Math.max(1, Math.floor(sizeModule)));
  chunks.push(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, mod]));

  // fn=69: error correction L=48 M=49 Q=50 H=51
  chunks.push(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31]));

  // fn=80, 48: store data
  const pL = (bytes.length + 3) & 0xff;
  const pH = ((bytes.length + 3) >> 8) & 0xff;
  chunks.push(
    concatBytes(
      new Uint8Array([GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30]),
      bytes,
    ),
  );

  // fn=81, 48: print QR
  chunks.push(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30]));

  return concatBytes(...chunks);
}
